/**
 * `next` — the intent parameter, and this repo is its only validator.
 *
 * `frontend-landing` composes it client-side from its own path plus its active
 * facets plus a scroll offset and navigates to
 * `https://my.shahedni.com/login?next=<encoded>`. Anything that is not a path
 * on THIS origin is discarded in favour of `/`: a crafted
 * `/login?next=https://evil.example` would otherwise return the reader
 * off-origin immediately after they authenticated.
 *
 * The UI/UX pack draws the login form's hidden field as `return_to`. One
 * value, two spellings — the QUERY PARAMETER is the binding one (§23 Q7).
 */

export const NEXT_PARAM = 'next';
export const NEXT_FORM_FIELD = 'return_to';

const FALLBACK = '/';

/** Returns a same-origin path, or `/` when the candidate is anything else. */
export function validateNext(candidate: string | null | undefined): string {
  if (!candidate) return FALLBACK;

  let decoded = candidate;
  try {
    decoded = decodeURIComponent(candidate);
  } catch {
    return FALLBACK;
  }

  // A protocol-relative value (`//evil.example`) is off-origin despite the
  // leading slash, and a backslash variant is normalised by some engines.
  if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.startsWith('/\\')) {
    return FALLBACK;
  }

  try {
    const resolved = new URL(decoded, window.location.origin);
    if (resolved.origin !== window.location.origin) return FALLBACK;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return FALLBACK;
  }
}

/** `/titles/x?a=1` → `/login?next=%2Ftitles%2Fx%3Fa%3D1` */
export function loginPathFor(currentPath: string): string {
  return `/login?${NEXT_PARAM}=${encodeURIComponent(currentPath)}`;
}

export function currentPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}
